/**
 * ============================================================================
 * API SERVICE - Centralized API calls with caching
 * ============================================================================
 * 
 * All API calls go through this service which implements:
 * - Cache-first strategy
 * - TTL-based cache expiration
 * - Automatic cache invalidation
 * - Console logging for debugging
 * 
 * ============================================================================
 */

import cacheManager from '../utils/cacheManager';

const BASE_URL = 'https://taskscheduler-backend.onrender.com/api/v1';

// Cache TTL configurations (in milliseconds)
const CACHE_TTL = {
  MEMBERS: 10 * 60 * 1000,        // 10 minutes - rarely changes
  TASKS: 3 * 60 * 1000,           // 3 minutes - changes more frequently
  TASK_STATS: 5 * 60 * 1000,      // 5 minutes
  MEMBER_PROFILE: 10 * 60 * 1000, // 10 minutes
  TEAMS: 30 * 60 * 1000,          // 30 minutes - static data
};

/**
 * Get authorization headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Generic fetch with cache
 * @param {string} cacheKey - Unique cache key
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @param {number} ttl - Cache TTL in milliseconds
 * @returns {Promise<any>} - API response data
 */
const fetchWithCache = async (cacheKey, url, options = {}, ttl = CACHE_TTL.MEMBERS) => {
  // Check cache first
  const cachedData = cacheManager.get(cacheKey);
  if (cachedData) {
    console.log(`✅ [API] Loaded from cache: ${cacheKey}`);
    return cachedData;
  }

  // Fetch from API
  console.log(`🌐 [API] Fetching fresh data: ${cacheKey}`);
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    const result = await response.json();

    if (response.ok) {
      // Cache the successful response
      cacheManager.set(cacheKey, result, ttl);
      console.log(`💾 [API] Cached response: ${cacheKey}`);
      return result;
    } else {
      console.error(`❌ [API] Error fetching ${cacheKey}:`, result.message);
      throw new Error(result.message || `API Error: ${response.status}`);
    }
  } catch (error) {
    console.error(`❌ [API] Network error fetching ${cacheKey}:`, error);
    throw error;
  }
};

/**
 * ============================================================================
 * MEMBERS API
 * ============================================================================
 */

/**
 * Fetch all members/users
 * GET /auth/all-users
 */
export const fetchAllMembers = async (forceRefresh = false) => {
  const cacheKey = 'all-users';
  
  if (forceRefresh) {
    console.log('🔄 [API] Force refresh: Invalidating members cache');
    cacheManager.delete(cacheKey);
  }

  // Check cache first
  const cached = cacheManager.get(cacheKey);
  if (cached && !forceRefresh) {
    return cached;
  }

  let result = null;

  // Try primary endpoint: /auth/all-users
  try {
    const res = await fetch(`${BASE_URL}/auth/all-users`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    if (res.ok) {
      result = await res.json();
    }
  } catch (err) {
    console.warn('⚠️ [API] /auth/all-users failed, attempting fallback endpoints:', err);
  }

  // Fallback endpoint 1: /users
  if (!result || (!result.data && !result.users && !Array.isArray(result))) {
    try {
      const res = await fetch(`${BASE_URL}/users`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        result = await res.json();
      }
    } catch (e) {
      console.warn('⚠️ [API] /users fallback failed:', e);
    }
  }

  // Fallback endpoint 2: /users/all
  if (!result || (!result.data && !result.users && !Array.isArray(result))) {
    try {
      const res = await fetch(`${BASE_URL}/users/all`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        result = await res.json();
      }
    } catch (e) {
      console.warn('⚠️ [API] /users/all fallback failed:', e);
    }
  }
  
  console.log('📦 [API] fetchAllMembers result:', result);
  
  if (result) {
    const userArray = (result.data && result.data.users) || 
                      (Array.isArray(result.data) ? result.data : null) || 
                      (Array.isArray(result.users) ? result.users : null) ||
                      (Array.isArray(result) ? result : null);

    if (userArray && userArray.length > 0) {
      console.log(`📦 [API] Transforming ${userArray.length} users`);
      const transformedMembers = userArray.map(m => ({
        id: m._id || m.id,
        name: m.fullname || m.name || 'Member',
        team: m.team || 'General',
        email: m.email || '',
        roll: m.roll_no || m.rollNumber || m.roll || 'N/A',
        image: m.avatar || m.image,
        assigned_task: m.assigned_task || [],
        taskCount: Array.isArray(m.assigned_task) 
          ? m.assigned_task.length 
          : (typeof m.totalTask === 'number' ? m.totalTask : (m.taskCount || 0)),
        role: m.role || 'user',
        section: m.classes || m.section || 'N/A',
        year: m.year || 'N/A',
      }));

      const finalResult = {
        ...result,
        data: {
          ...(typeof result.data === 'object' && !Array.isArray(result.data) ? result.data : {}),
          users: userArray,
          transformedMembers,
        },
      };

      cacheManager.set(cacheKey, finalResult, CACHE_TTL.MEMBERS);
      return finalResult;
    }
  }

  console.warn('⚠️ [API] No users data found in endpoints, returning empty structure');
  return { success: false, data: { users: [], transformedMembers: [] } };
};

/**
 * Fetch member profile by ID
 * GET /task/member/:id
 */
export const fetchMemberProfile = async (memberId, forceRefresh = false) => {
  const cacheKey = `member-profile-${memberId}`;
  
  if (forceRefresh) {
    console.log(`🔄 [API] Force refresh: Invalidating member profile ${memberId}`);
    cacheManager.delete(cacheKey);
  }

  return await fetchWithCache(
    cacheKey,
    `${BASE_URL}/task/member/${memberId}`,
    { method: 'GET' },
    CACHE_TTL.MEMBER_PROFILE
  );
};

/**
 * ============================================================================
 * TASKS API
 * ============================================================================
 */

/**
 * Fetch tasks with filters
 * GET /task/
 */
export const fetchTasks = async (searchQuery = '', filterType = '', forceRefresh = false) => {
  // Create unique cache key based on filters
  const cacheKey = `tasks-${searchQuery}-${filterType}`;
  
  if (forceRefresh) {
    console.log('🔄 [API] Force refresh: Invalidating tasks cache');
    cacheManager.invalidatePattern('tasks');
  }

  const params = new URLSearchParams();
  if (searchQuery) params.append('query', searchQuery);
  if (filterType) params.append('filter_Type', filterType);
  
  const url = `${BASE_URL}/task/${params.toString() ? '?' + params.toString() : ''}`;

  return await fetchWithCache(
    cacheKey,
    url,
    { method: 'GET' },
    CACHE_TTL.TASKS
  );
};

/**
 * Fetch task statistics
 * GET /task/stats
 */
export const fetchTaskStats = async (forceRefresh = false) => {
  const cacheKey = 'task-stats';
  
  if (forceRefresh) {
    console.log('🔄 [API] Force refresh: Invalidating task stats');
    cacheManager.delete(cacheKey);
  }

  return await fetchWithCache(
    cacheKey,
    `${BASE_URL}/task/stats`,
    { method: 'GET' },
    CACHE_TTL.TASK_STATS
  );
};

/**
 * Update task status
 * PATCH /task/:id
 * Invalidates task cache after update
 */
export const updateTaskStatus = async (taskId, newStatus) => {
  const url = `${BASE_URL}/task/${taskId}`;
  
  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status: newStatus }),
  });

  const result = await response.json();

  if (response.ok && result.success) {
    console.log('✅ [API] Task status updated, invalidating cache');
    // Invalidate all task-related cache
    cacheManager.invalidatePattern('tasks');
    cacheManager.invalidatePattern('task-stats');
    cacheManager.invalidatePattern('member-profile'); // Member profiles contain task lists
  }

  return result;
};

/**
 * Delete task
 * DELETE /task/:id
 * Invalidates task cache after deletion
 */
export const deleteTask = async (taskId) => {
  const url = `${BASE_URL}/task/${taskId}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const result = await response.json();

  if (response.ok) {
    console.log('✅ [API] Task deleted, invalidating cache');
    // Invalidate all task-related cache
    cacheManager.invalidatePattern('tasks');
    cacheManager.invalidatePattern('task-stats');
    cacheManager.invalidatePattern('member-profile');
  }

  return result;
};

/**
 * Create new task
 * POST /task/create
 * Invalidates task cache after creation
 */
export const createTask = async (taskData) => {
  const url = `${BASE_URL}/task/`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(taskData),
  });

  const result = await response.json();

  if (response.ok) {
    console.log('✅ [API] Task created, invalidating cache');
    // Invalidate all task-related cache
    cacheManager.invalidatePattern('tasks');
    cacheManager.invalidatePattern('task-stats');
    cacheManager.invalidatePattern('member-profile');
  }

  return result;
};

/**
 * ============================================================================
 * USER/MEMBER MUTATIONS
 * ============================================================================
 */

/**
 * Create new member
 * POST /auth/register
 * Invalidates member cache after creation
 */
export const createMember = async (memberData) => {
  const url = `${BASE_URL}/auth/register`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(memberData),
  });

  const result = await response.json();

  if (response.ok) {
    console.log('✅ [API] Member created, invalidating cache');
    // Invalidate all member-related cache
    cacheManager.invalidatePattern('members');
    cacheManager.invalidatePattern('all-users');
  }

  return result;
};

/**
 * Update member
 * PATCH /auth/user/:id (with fallback to PUT)
 * Invalidates member cache after update
 */
export const updateMember = async (memberId, memberData) => {
  const url = `${BASE_URL}/auth/user/${memberId}`;
  const isFormData = typeof FormData !== 'undefined' && memberData instanceof FormData;
  
  let body;
  let headers;

  if (isFormData) {
    body = memberData;
    headers = {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    };
  } else {
    // If object, convert to FormData as backend multer handles multipart/form-data
    const fd = new FormData();
    Object.keys(memberData).forEach(key => {
      if (memberData[key] !== undefined && memberData[key] !== null) {
        fd.append(key, memberData[key]);
      }
    });
    body = fd;
    headers = {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    };
  }

  let response = await fetch(url, {
    method: 'PATCH',
    headers: headers,
    body: body,
  });

  // Fallback to PUT if PATCH is not supported
  if (!response.ok && response.status === 405) {
    response = await fetch(url, {
      method: 'PUT',
      headers: headers,
      body: body,
    });
  }

  const result = await response.json();

  if (response.ok) {
    console.log('✅ [API] Member updated, invalidating cache');
    cacheManager.invalidatePattern('members');
    cacheManager.invalidatePattern('all-users');
    cacheManager.delete(`member-profile-${memberId}`);
  }

  return result;
};

/**
 * Bulk update multiple members with progress feedback
 * @param {Array<{id: string, name?: string, updates: object}>} membersToUpdate
 * @param {Function} onProgress - Callback (completed, total, currentMemberName)
 */
export const bulkUpdateMembers = async (membersToUpdate, onProgress = () => {}) => {
  const total = membersToUpdate.length;
  let completed = 0;
  const successful = [];
  const failed = [];

  for (const item of membersToUpdate) {
    const memberId = item.id || item._id;
    const memberName = item.name || item.fullname || 'Member';
    const updates = item.updates || item;

    onProgress(completed, total, memberName);

    try {
      const res = await updateMember(memberId, updates);
      if (res && (res.statusCode === 200 || res.success || !res.message?.includes('fail'))) {
        successful.push({ id: memberId, name: memberName });
      } else {
        failed.push({ id: memberId, name: memberName, error: res?.message || 'Update rejected' });
      }
    } catch (err) {
      failed.push({ id: memberId, name: memberName, error: err.message });
    }

    completed += 1;
    onProgress(completed, total, memberName);
  }

  // Invalidate all member caches
  cacheManager.invalidatePattern('members');
  cacheManager.invalidatePattern('all-users');

  return { successful, failed, total };
};

/**
 * Delete member
 * DELETE /auth/user/:id
 * Invalidates member cache after deletion
 */
export const deleteMember = async (memberId) => {
  const url = `${BASE_URL}/auth/user/${memberId}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const result = await response.json();

  if (response.ok) {
    console.log('✅ [API] Member deleted, invalidating cache');
    // Invalidate all member-related cache
    cacheManager.invalidatePattern('members');
    cacheManager.invalidatePattern('all-users');
    cacheManager.delete(`member-profile-${memberId}`);
  }

  return result;
};

/**
 * ============================================================================
 * UTILITY FUNCTIONS
 * ============================================================================
 */

/**
 * Manually invalidate cache
 */
export const invalidateCache = {
  members: () => {
    cacheManager.invalidatePattern('members');
    cacheManager.invalidatePattern('all-users');
  },
  tasks: () => {
    cacheManager.invalidatePattern('tasks');
    cacheManager.invalidatePattern('task-stats');
  },
  memberProfile: (memberId) => {
    cacheManager.delete(`member-profile-${memberId}`);
  },
  all: () => {
    cacheManager.clear();
  },
};

const apiService = {
  fetchAllMembers,
  fetchMemberProfile,
  fetchTasks,
  fetchTaskStats,
  updateTaskStatus,
  deleteTask,
  createTask,
  createMember,
  updateMember,
  deleteMember,
  invalidateCache,
};

export default apiService;
